import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Utils, DateTools } from '@treezor/design-system';

import { ISctrSimulationModalProps } from '@/features/wallets/components/SctrSimulationModal';
import { IWalletDetailsModalProps } from '@/features/wallets/components/WalletDetailsModal';
import { IWalletEditionModalProps } from '@/features/wallets/components/WalletEditionModal';
import { IWalletStatementModalProps } from '@/features/wallets/components/WalletStatementModal';
import { IWallet, walletStatusList } from '@/features/wallets/store/models/wallet.model';
import {
  allDadshboardOptions,
  IOperationSimulationModalProps,
  DashboardConfigSettingsWalletsOptions,
  IUseWalletMenu,
  TDashboardConfigSettingsWalletsOptions,
} from '@/features/wallets/types/index';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';
import { useAccesControl } from '@/hooks/useAccessControl';
import {
  deactivateWallet,
  getBalanceCertificate,
  getClosureCertificate,
  getDomiciliationCertificate,
  getWalletAccountDetail,
} from '@/features/wallets/store/actions/wallet.actions';
import {
  getAgentWalletAccountDetail,
  getAgentBalanceCertificate,
  getAgentClosureCertificate,
  getAgentDomiciliationCertificate,
} from '@/features/wallets/store/actions/agent-wallets.actions';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { openModal } from '@/store/actions/core.actions';
import { selectCurrentProfileWalletByUserId, selectProfileWalletByUserIdAndWalletId } from '@/store/selectors';
import { ActionMenuItem } from '@/ui-kit/action-menu/ActionMenuItem';
import { IUser } from '@/features/user/store/user.model';
import { IAgentProfileWallet } from '@/features/wallets/store/types/agent-wallets.types';

export const useWalletMenu = (
  wallet: IWallet,
  user: number | IUser,
  options: TDashboardConfigSettingsWalletsOptions[],
  onSelect: () => void,
  agentProfileWallet?: IAgentProfileWallet,
): IUseWalletMenu => {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const { hasAccessToFeature } = useAccesControl();

  const dispatch = useAppDispatch();
  const [isAccountDetailDownloaded, setIsAccountDetailDownloaded] = useState(false);
  const [isDomiciliationCertifDownloaded, setIsDomiciliationCertifDownloaded] = useState(false);
  const [isBalanceCertifDownloaded, setIsBalanceCertifDownloaded] = useState(false);
  const [isClosureCertifDownloaded, setIsClosureCertifDownloaded] = useState(false);

  const isDeactivationEnable = wallet.solde === 0.0;
  const isCanceledWallet = wallet.walletStatus === walletStatusList.CANCELED;

  const userId = typeof user === 'number' ? user : user.userId;
  const isAgentContext = !!agentProfileWallet;

  const hasOperations = useAppSelector((state) =>
    Boolean(selectCurrentProfileWalletByUserId(state, userId)?.state?.paginateOperations?.state?.elements.length),
  );

  const walletAccountDetail = isAgentContext
    ? agentProfileWallet?.accountDetails?.state
    : useAppSelector(
        (state) => selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.accountDetails?.state,
      );

  const domiciliationCertificate = isAgentContext
    ? agentProfileWallet?.domiciliationCertificate?.state
    : useAppSelector(
        (state) =>
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.domiciliationCertificate
            ?.state,
      );

  const balanceCertificate = isAgentContext
    ? agentProfileWallet?.balanceCertificate?.state
    : useAppSelector(
        (state) =>
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.balanceCertificate?.state,
      );

  const closureCertificate = isAgentContext
    ? agentProfileWallet?.closureCertificate?.state
    : useAppSelector(
        (state) =>
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.closureCertificate?.state,
      );

  const isAccountDetailLoading = isAgentContext
    ? agentProfileWallet?.accountDetails?.isLoading
    : useAppSelector(
        (state) =>
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.accountDetails?.isLoading,
      );

  const isCertificateLoading = isAgentContext
    ? agentProfileWallet?.domiciliationCertificate?.isLoading ||
      agentProfileWallet?.balanceCertificate?.isLoading ||
      agentProfileWallet?.closureCertificate?.isLoading
    : useAppSelector(
        (state) =>
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.domiciliationCertificate
            ?.isLoading ||
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.balanceCertificate
            ?.isLoading ||
          selectProfileWalletByUserIdAndWalletId(state, userId, wallet.walletId)?.state?.closureCertificate?.isLoading,
      );

  useEffect(() => {
    if (walletAccountDetail && isAccountDetailDownloaded) {
      Utils.downloadFile(
        walletAccountDetail.link,
        `wallet_account_detail_${DateTools.format(DateTools.now(), 'file')}`,
      );
    }
  }, [isAccountDetailDownloaded, wallet.walletId, walletAccountDetail]);

  useEffect(() => {
    if (domiciliationCertificate && isDomiciliationCertifDownloaded) {
      Utils.downloadFile(
        domiciliationCertificate.link,
        `domiciliation_certificate${DateTools.format(DateTools.now(), 'file')}`,
      );
    }
  }, [domiciliationCertificate, isDomiciliationCertifDownloaded, wallet.walletId]);

  useEffect(() => {
    if (balanceCertificate && isBalanceCertifDownloaded) {
      Utils.downloadFile(balanceCertificate.link, `balance_certificate${DateTools.format(DateTools.now(), 'file')}`);
    }
  }, [balanceCertificate, isBalanceCertifDownloaded, wallet.walletId]);

  useEffect(() => {
    if (closureCertificate && isClosureCertifDownloaded) {
      Utils.downloadFile(closureCertificate.link, `closure_certificate${DateTools.format(DateTools.now(), 'file')}`);
    }
  }, [closureCertificate, isClosureCertifDownloaded, wallet.walletId]);

  const handleWalletAccountDetailDownload = () => {
    setIsAccountDetailDownloaded(true);
    if (isAgentContext) {
      dispatch(getAgentWalletAccountDetail(wallet.walletId));
    } else {
      dispatch(getWalletAccountDetail(wallet.walletId, userId));
    }
  };

  const handleWalletDomiciliationCertificateDownload = () => {
    setIsDomiciliationCertifDownloaded(true);
    if (isAgentContext) {
      dispatch(getAgentDomiciliationCertificate(wallet.walletId));
    } else {
      dispatch(getDomiciliationCertificate(wallet.walletId, userId));
    }
  };

  const handleWalletBalanceCertificateDownload = () => {
    setIsBalanceCertifDownloaded(true);
    if (isAgentContext) {
      dispatch(getAgentBalanceCertificate(wallet.walletId));
    } else {
      dispatch(getBalanceCertificate(wallet.walletId, userId));
    }
  };

  const handleWalletClosureCertificateDownload = () => {
    setIsClosureCertifDownloaded(true);
    if (isAgentContext) {
      dispatch(getAgentClosureCertificate(wallet.walletId));
    } else {
      dispatch(getClosureCertificate(wallet.walletId, userId));
    }
  };

  const toggleDialogWalletRemove = () => {
    WalletDeactivationDialog.toggle();
  };

  const handleWalletDeactivation = () => {
    if (wallet.walletId) {
      dispatch(deactivateWallet(wallet));
    }
    toggleDialogWalletRemove();
  };

  const WalletDeactivationDialog = useConfirmationDialog({
    title: t('pages.currentProfiles.wallets.wallet.cancel.deactivateWallet', {
      walletName: wallet.eventName,
      fullName: `${wallet.userFirstname} ${wallet.userLastname}`,
    }),
    description: isDeactivationEnable
      ? t('pages.currentProfiles.wallets.wallet.cancel.title')
      : t('pages.currentProfiles.wallets.wallet.cancel.sub'),

    confirm: {
      disabled: !isDeactivationEnable,
      label: t('pages.currentProfiles.wallets.wallet.cancel.confirmationDeactivation'),
      action: handleWalletDeactivation,
      color: 'danger',
    },
  });

  const toggleWalletStatement = () => {
    dispatch(openModal<IWalletStatementModalProps>('walletStatementModal', { wallet, userId }));
  };

  const toggleEditWallet = () =>
    dispatch(
      openModal<IWalletEditionModalProps>('walletEditionModal', {
        wallet,
        hasOperations,
        user: user as IUser,
      }),
    );

  const toggleDetailsWallet = () => {
    dispatch(
      openModal<IWalletDetailsModalProps>('walletDetailModal', {
        wallet,
      }),
    );
  };

  const toggleSimulateSCTR = () =>
    dispatch(
      openModal<ISctrSimulationModalProps>('sctrSimulationModal', {
        wallet,
      }),
    );

  const toggleSimulateOperation = () =>
    dispatch(
      openModal<IOperationSimulationModalProps>('operationSimulationModal', {
        wallet,
      }),
    );

  const handleSelectItem = useCallback(
    (then: () => void) => {
      onSelect();
      then();
    },
    [onSelect],
  );

  const configActions: TDashboardConfigSettingsWalletsOptions[] =
    config.settings.wallets.options || allDadshboardOptions;

  const actionFilter =
    (!!options && options.length > 0 && configActions.filter((actionName) => options.includes(actionName))) ||
    configActions;

  const downloadActions = [];

  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.DOMICILIATIONCERTIFICATE) &&
    hasAccessToFeature(featuresSlugs.DOMICILIATION_CERTIFICATE) &&
    !isCanceledWallet
  ) {
    downloadActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.domiciliationCertificate')}
        className="py-2 px-2 m-0"
        key="domiciliationCertificate"
        testId="domiciliation-certificate"
        onClick={() => handleSelectItem(handleWalletDomiciliationCertificateDownload)}
      />,
    );
  }

  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.BALANCECERTIFICATE) &&
    hasAccessToFeature(featuresSlugs.BALANCE_CERTIFICATE) &&
    !isCanceledWallet
  ) {
    downloadActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.balanceCertificate')}
        className="py-2 px-2 m-0"
        key="balanceCertificate"
        testId="balance-certificate"
        onClick={() => handleSelectItem(handleWalletBalanceCertificateDownload)}
      />,
    );
  }

  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.CLOSURECERTIFICATE) &&
    hasAccessToFeature(featuresSlugs.CLOSURE_CERTIFICATE) &&
    isCanceledWallet
  ) {
    downloadActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.closureCertificate')}
        className="py-2 px-2 m-0"
        key="closureCertificate"
        testId="closure-certificate"
        onClick={() => handleSelectItem(handleWalletClosureCertificateDownload)}
      />,
    );
  }

  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.ACCOUNTDETAILS) &&
    hasAccessToFeature(featuresSlugs.ACCOUNT_DETAIL)
  ) {
    downloadActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.accountDetail')}
        className="py-2 px-2 m-0"
        onClick={() => handleSelectItem(handleWalletAccountDetailDownload)}
        key="accountDetail"
        testId="account-detail"
      />,
    );
  }
  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.STATEMENT) &&
    hasAccessToFeature(featuresSlugs.STATEMENT)
  ) {
    downloadActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.statement')}
        className="py-2 px-2 m-0"
        onClick={() => handleSelectItem(toggleWalletStatement)}
        key="statement"
        testId="statement"
      />,
    );
  }

  const simulateActions = [];

  if (actionFilter.includes(DashboardConfigSettingsWalletsOptions.SIMULATESCTR)) {
    simulateActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.incomingBankTransfert')}
        className="py-2 px-2 m-0"
        onClick={() => handleSelectItem(toggleSimulateSCTR)}
        key="simulateSCTR"
        testId="simulate-SCTR"
      />,
    );
  }

  if (actionFilter.includes(DashboardConfigSettingsWalletsOptions.SIMULATEOPERATION)) {
    simulateActions.push(
      <ActionMenuItem
        label={t('pages.currentProfiles.wallets.wallet.menu.operations')}
        className="py-2 px-2 m-0"
        onClick={() => handleSelectItem(toggleSimulateOperation)}
        key="simulateOperation"
        testId="simulate-operation"
      />,
    );
  }

  const walletActionsA = [];
  const walletActionsB = [];

  if (downloadActions.length > 0) {
    walletActionsA.push({
      component: (
        <ActionMenuItem
          label={t('pages.currentProfiles.wallets.wallet.menu.downloadDocument')}
          icon="download"
          subMenu={downloadActions}
          testId="download-document"
          key="downloadDocument"
        />
      ),
    });
  }

  if (
    simulateActions.length > 0 &&
    config.site.environment.toLowerCase() === 'sandbox' &&
    !isCanceledWallet &&
    hasAccessToFeature(featuresSlugs.SIMULATION)
  ) {
    walletActionsA.push({
      component: (
        <ActionMenuItem
          label={t('pages.currentProfiles.wallets.wallet.menu.simulate')}
          icon="modeling"
          subMenu={simulateActions}
          testId="simulate"
          key="simulate"
        />
      ),
    });
  }

  if (
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.DETAILS) &&
    hasAccessToFeature(featuresSlugs.WALLET_DETAILS)
  ) {
    walletActionsB.push({
      component: (
        <ActionMenuItem
          label={t('pages.currentProfiles.wallets.wallet.menu.details')}
          icon="account_balance_wallet"
          testId="wallet-details"
          key="wallet-details"
          onClick={() => handleSelectItem(toggleDetailsWallet)}
        />
      ),
    });
  }

  if (
    !isCanceledWallet &&
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.EDIT) &&
    hasAccessToFeature(featuresSlugs.EDIT_WALLET) &&
    typeof user !== 'number'
  ) {
    walletActionsB.push({
      component: (
        <ActionMenuItem
          label={t('pages.currentProfiles.wallets.wallet.menu.edit')}
          icon="edit"
          testId="wallet-edit"
          key="wallet-edit"
          onClick={() => handleSelectItem(toggleEditWallet)}
        />
      ),
    });
  }

  if (
    !isCanceledWallet &&
    actionFilter.includes(DashboardConfigSettingsWalletsOptions.DEACTIVATE) &&
    hasAccessToFeature(featuresSlugs.DEACTIVATE_WALLET)
  ) {
    walletActionsB.push({
      component: (
        <ActionMenuItem
          label={t('pages.currentProfiles.wallets.wallet.menu.deactivate')}
          icon="cancel"
          color="danger"
          testId="wallet-deactivate"
          key="wallet-deactivate"
          onClick={() => handleSelectItem(WalletDeactivationDialog.toggle)}
        />
      ),
    });
  }

  let walletActions = [...walletActionsA];

  if (walletActionsB.length > 0) {
    walletActions.push({
      component: <div className="w-full h-2 border-t-2 border-primary m-3"></div>,
    });
  }

  walletActions = [...walletActions, ...walletActionsB];

  return {
    walletActions: walletActions.map((action) => ({ ...action, hasCustomMargin: true })),
    isDisabled: walletActions.length === 0,
    WalletDeactivationDialog,
    isLoading: isAccountDetailLoading || isCertificateLoading,
  };
};
